import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "step",
        "progress",
        "stepError",
        "paymentToggle",
        "priceKindSelect",
        "paymentInstructions",
        "dateSelectionToggle",
        "dateVotingMultipleContainer",
        "dateVotingMultipleInput",
        "dateVotingDeadlineContainer",
        "dateVotingDeadlineInput",
        "submitButton"
    ]
    static values = {
        currentStep: Number,
        requiredFieldMessage: String,
        invalidEmailMessage: String,
        stepErrorMessage: String,
        submitLoadingText: String
    }

    connect() {
        this.isSubmitting = false
        this.currentStepValue = 0
        this.showStep(0)
        this.updatePaymentVisibility()
        this.updateDateVotingVisibility()

        this.form = this.element.querySelector("form")
        if (this.form) {
            this.form.addEventListener("submit", this.handleSubmit)
        }

        this.element.addEventListener("keydown", (e) => {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                e.preventDefault()
            }
        })
    }

    disconnect() {
        if (this.form) {
            this.form.removeEventListener("submit", this.handleSubmit)
        }
    }

    next() {
        if (this.validateStep(this.currentStepValue)) {
            let nextIndex = this.currentStepValue + 1
            if (nextIndex === 2 && !this.paymentRequired()) {
                nextIndex = 3
            }

            if (nextIndex < this.stepTargets.length) {
                this.currentStepValue = nextIndex
                this.showStep(this.currentStepValue)
            }
        }
    }

    previous() {
        if (this.currentStepValue > 0) {
            let previousIndex = this.currentStepValue - 1
            if (this.currentStepValue === 3 && !this.paymentRequired()) {
                previousIndex = 1
            }

            this.currentStepValue = previousIndex
            this.showStep(this.currentStepValue)
        }
    }

    showStep(index) {
        this.stepTargets.forEach((el, i) => {
            el.classList.toggle("hidden", i !== index)
        })
        this.updateProgress(index)
    }

    updateProgress(index) {
        if (this.hasProgressTarget) {
            const total = this.stepTargets.length
            const percentage = ((index + 1) / total) * 100
            this.progressTarget.style.width = `${percentage}%`
        }
    }

    validateStep(index) {
        const stepEl = this.stepTargets[index]
        const inputs = stepEl.querySelectorAll("input, select, textarea")
        let valid = true
        let firstInvalid = null

        inputs.forEach(input => {
            if (input.disabled || input.type === "hidden") return

            this.clearInputError(input)
            const value = input.value?.trim() || ""
            const requiredInvalid = input.hasAttribute("required") && value === ""
            const emailInvalid = input.type === "email" && value !== "" && !input.checkValidity()

            if (requiredInvalid || emailInvalid) {
                valid = false
                if (!firstInvalid) firstInvalid = input

                const message = emailInvalid
                    ? this.invalidEmailMessageValue || this.requiredFieldMessageValue
                    : this.requiredFieldMessageValue
                this.showInputError(input, message)
            }
        })

        this.toggleStepError(index, !valid)

        if (!valid && firstInvalid) {
            firstInvalid.focus()
            firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" })
        }

        return valid
    }

    togglePayment() {
        this.updatePaymentVisibility()
    }

    updatePaymentVisibility() {
        if (this.hasPaymentToggleTarget && this.hasPaymentInstructionsTarget) {
            const showPaymentInstructions = this.requiresPaymentInstructions()
            const paymentStep = this.stepTargets[2]
            if (paymentStep) {
                paymentStep.classList.toggle("opacity-60", !this.paymentRequired())
            }

            this.paymentInstructionsTarget.classList.toggle("hidden", !showPaymentInstructions)
            const input = this.paymentInstructionsTarget.querySelector("textarea")
            if (input) {
                input.disabled = !showPaymentInstructions
                input.required = showPaymentInstructions
                if (!showPaymentInstructions) {
                    input.value = ""
                }
            }
        }
    }

    toggleDateSelection() {
        this.updateDateVotingVisibility()
    }

    updateDateVotingVisibility() {
        if (!this.hasDateSelectionToggleTarget) return

        const enabled = this.dateSelectionToggleTarget.checked

        if (this.hasDateVotingMultipleContainerTarget) {
            this.dateVotingMultipleContainerTarget.classList.toggle("hidden", !enabled)
        }
        if (this.hasDateVotingMultipleInputTarget) {
            this.dateVotingMultipleInputTarget.disabled = !enabled
            if (!enabled) {
                this.dateVotingMultipleInputTarget.checked = false
            }
        }

        if (this.hasDateVotingDeadlineContainerTarget) {
            this.dateVotingDeadlineContainerTarget.classList.toggle("hidden", !enabled)
        }
        if (this.hasDateVotingDeadlineInputTarget) {
            this.dateVotingDeadlineInputTarget.disabled = !enabled
            if (!enabled) {
                this.dateVotingDeadlineInputTarget.value = ""
            }
        }
    }

    handleSubmit = (event) => {
        if (this.isSubmitting) {
            event.preventDefault()
            return
        }

        this.isSubmitting = true
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true
            this.submitButtonTarget.dataset.originalText = this.submitButtonTarget.textContent
            this.submitButtonTarget.textContent = this.submitLoadingTextValue || this.submitButtonTarget.textContent
        }
    }

    toggleStepError(index, show) {
        if (!this.stepErrorTargets[index]) return
        this.stepErrorTargets[index].classList.toggle("hidden", !show)
        this.stepErrorTargets[index].textContent = show ? (this.stepErrorMessageValue || "") : ""
    }

    showInputError(input, message) {
        input.classList.add("input-error")
        input.setAttribute("aria-invalid", "true")

        const errorId = this.errorIdFor(input)
        let errorElement = this.element.querySelector(`#${errorId}`)
        if (!errorElement) {
            errorElement = document.createElement("p")
            errorElement.id = errorId
            errorElement.className = "text-xs text-red-300 mt-2"
            input.insertAdjacentElement("afterend", errorElement)
        }
        errorElement.textContent = message
    }

    clearInputError(input) {
        input.classList.remove("input-error")
        input.removeAttribute("aria-invalid")

        const errorElement = this.element.querySelector(`#${this.errorIdFor(input)}`)
        if (errorElement) {
            errorElement.remove()
        }
    }

    errorIdFor(input) {
        const identifier = input.id || input.name || "field"
        return `wizard-error-${identifier.replace(/[^a-zA-Z0-9_-]/g, "-")}`
    }

    paymentRequired() {
        return this.hasPaymentToggleTarget && this.paymentToggleTarget.checked
    }

    requiresPaymentInstructions() {
        const freeKindSelected = this.hasPriceKindSelectTarget && this.priceKindSelectTarget.value === "free"
        return this.paymentRequired() && !freeKindSelected
    }
}
